<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';


import {computed, nextTick, onMounted, ref} from 'vue'
import {PropertyStrategyThemeType} from '@toolify/client/src/modules/tableItems/enum/PropertyStrategyThemeType'
import {InputDestinationType} from '@toolify/client/src/modules/forms/enum/InputDestinationType'
import {InputColorType} from '@toolify/client/src/modules/forms/enum/InputColorType'
import {InputType} from '@toolify/client/src/modules/forms/enum/InputType'
import {InputBackgroundType} from '@toolify/client/src/modules/forms/enum/InputBackgroundType'
import {AlignmentType} from '@toolify/server/src/enum/AlignmentType'
import {useKeyboardShortcuts} from '@toolify/client/src/modules/keyboard/composables/useKeyboardShortcuts'
import {CommonShortcutType} from '@toolify/client/src/stores/KeyboardShortcutStore/enum/CommonShortcutType'

interface Props {
  type?: InputType
  focusOnMounted?: boolean
  selectOnMounted?: boolean
  modelValue?: string|number
  background?: InputBackgroundType
  theme?: PropertyStrategyThemeType
  destination?: InputDestinationType
  color?: InputColorType
  placeholder?: string
  showOnClick?: boolean
  textAlignment?: AlignmentType
  isEditing?: boolean
  isHovered?: boolean
  isDisabled?: boolean
  isReadonly?: boolean
  isEdited?: boolean
  hasError?: boolean
  fullWidth?: boolean
  showClearButton?: boolean
  inputSize?: number
  fitContent?: boolean
}

interface Emits {
  (e: 'update:modelValue', value: string|number)
  (e: 'update:isEditing', value: boolean)
  (e: 'update:isHovered', value: boolean)
  (e: 'input', value: string|number, event: Event)
  (e: 'change', value: string, event: Event)
  (e: 'focus', event: FocusEvent)
  (e: 'blur', value: string, event: FocusEvent)
  (e: 'enter', value: string, event: KeyboardEvent)
  (e: 'tab', value: string, event: KeyboardEvent)
}

const props = withDefaults(defineProps<Props>(), {
  type: InputType.Text,
  focusOnMounted: false,
  selectOnMounted: false,
  modelValue: '',
  theme: PropertyStrategyThemeType.Primary,
  placeholder: '',
  showOnClick: false,
  background: InputBackgroundType.Normal,
  destination: null,
  color: InputColorType.Neutral,
  textAlignment: AlignmentType.Left,
  isDisabled: false,
  isReadonly: false,
  isEdited: false,
  hasError: false,
  fullWidth: false,
  showClearButton: false,
  inputSize: 20,
  fitContent: false,
})

const emit = defineEmits<Emits>()

const inputClasses = computed(() => {
  return [
    'input',
    props.destination ? null : `theme-${props.theme}`,
    props.destination ? null : `background-${props.background}`,
    props.destination ? `destination-${props.destination}` : null,
    props.destination ? `color-${props.color}` : null,
    {
      'is-editing': props.isEditing,
      'is-disabled': props.isDisabled,
      'is-readonly': props.isReadonly,
      'is-edited': props.isEdited,
      'has-error': props.hasError,
      'fit-content': props.fitContent,
    },
  ]
})

const onInput = (event) => {
  let targetValue: string|number
  if(props.type === InputType.Number) {
    targetValue = event.target.value === '' ? '' : Number(event.target.value)
  } else {
    targetValue = String(event.target.value || '')
  }
  emit('update:modelValue', targetValue)
  emit('input', targetValue, event)
}

const getRawValue = (event: Event): string => {
  return (event.target as HTMLInputElement).value
}

const onChange = (event: Event) => {
  emit('change', getRawValue(event), event)
}

const onEnterKey = (event: KeyboardEvent) => {
  const rawValue = getRawValue(event)
  emit('change', rawValue, event)
  emit('enter', rawValue, event)
}

const onTabKey = (event: KeyboardEvent) => {
  emit('tab', getRawValue(event), event)
}

const input = _ref(null)

const value = _computed({
  get() {
    if(props.type === InputType.Number) {
      if(!props.modelValue) {
        return ''
      }
      return Number(props.modelValue)
    }
    if(!props.modelValue) {
      return ''
    }
    return String(props.modelValue)
  },
  set(value) {
    if(props.type === InputType.Number) {
      return emit('change', String(Number(value)), new Event('change'))
    }
    emit('change', String(value), new Event('change'))
  },
})

const isFocused = ref(false)

const inputOverridesTableShortcuts = () => {
  return {
    commonShortcutType: CommonShortcutType.InputFieldCatchAll,
    moreImportantThan: [CommonShortcutType.TableCellCatchAll],
  }
}

useKeyboardShortcuts([
  [[null], () => undefined],
], () => isFocused.value, {
  ...inputOverridesTableShortcuts(),
})

onMounted(async () => {
  if(props.focusOnMounted) {
    await nextTick(() => input.value?.focus())
  }
  if(props.selectOnMounted) {
    await nextTick(() =>{
      input.value?.focus()
      input.value?.select()

    })
  }
})

const focus = () => {
  input.value?.focus()
}

const blur = () => {
  input.value?.blur()
}

const select = () => {
  input.value?.select()
}

const isEditing = _computed({
  get() {
    return props.isEditing
  },
  set(value) {
    emit('update:isEditing', value)
  },
})

const onInputFocus = (event: FocusEvent) => {
  isFocused.value = true
  emit('focus', event)
  emit('update:isEditing', true)
}

const onInputBlur = (event: FocusEvent) => {
  isFocused.value = false
  emit('blur', getRawValue(event), event)
  emit('update:isEditing', false)
}

const isHovered = _computed({
  get() {
    return props.isHovered
  },
  set(value) {
    emit('update:isHovered', value)
  },
})

defineExpose({
  focus,
  blur,
  select,
  isFocused: isEditing.value,
  isHovered: isHovered.value,
})

const additionalStyles = _computed(() => {
  return {
    'text-align': props.textAlignment,
    'width': props.fullWidth ? '100%' : undefined,
    ...(props.showClearButton ? {'padding-right': '3rem'} : {}),
  }
})

const sizeAttribute = _computed(() => {
  if(props.fitContent) {
    return Math.max(String(value.value).length, 1)
  }
  return props.fullWidth ? undefined : props.inputSize
})

</script>

<template>
  <input
    :class="inputClasses"
    ref="input"
    :type="props.type"
    :placeholder="props.placeholder"
    :disabled="props.isDisabled || undefined"
    :readonly="props.isReadonly || undefined"
    @input="onInput"
    :value="value"
    v-bind="$attrs"
    @focus="onInputFocus"
    @change="onChange"
    :style="additionalStyles"
    @blur="onInputBlur"
    :size="sizeAttribute"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
    @keydown.enter="onEnterKey"
    @keydown.tab="onTabKey"
  />
</template>

<style scoped lang="scss">
  @use '../../../../styles/mixins' as mixins;
  @use '../../../../styles/variables' as variables;

  .input {
    @include mixins.inputStyle;

    &.fit-content {
      field-sizing: content;
    }
  }

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  input[type=number] {
    -moz-appearance: textfield;
  }

  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active{
    -webkit-background-clip: text;
    transition: background-color 5000s ease-in-out 0s;
    box-shadow: inset 0 0 20px 20px #23232329;
    color: map-get(variables.$colors, content-normal) !important;
    -webkit-text-fill-color: map-get(variables.$colors, content-normal) !important;
    caret-color: map-get(variables.$colors, content-normal) !important;
  }
</style>
